import { Component, h, Host, Element, Prop, State, Watch } from '@stencil/core';
import { HostElement } from '@stencil/core/internal';
import { WindowResizeService } from '../../services/window-resize-service';
import { ResponsiveBoolean, ResponsiveNumber, ResponsiveString } from '../../typings';
import { breakpointValues, getCurrentValue, getValueAtIndex } from '../../utils/utils';

/**
 * @slot mediaContent - Content displayed over media. If you want to reveal content on hover, use a tout block
 */
@Component({
  tag: 'content-image',
  styleUrl: 'content-image.scss',
  shadow: false,
})
export class ContentImage {
  @Element() hostElement: HostElement;
  /**
   * @type String
   * @responsive
   */
  @Prop({
    mutable: true,
    reflect: true,
  })
  classes: ResponsiveString;
  /**
   * @type Boolean
   */
  @Prop() lazyLoad: boolean = true;
  /**
   * @type Media
   * @responsive
   */
  @Prop({
    mutable: true,
    reflect: false,
  })
  src!: ResponsiveString;
  @State() activeSrc;
  /**
   * @type String
   * @responsive
   */
  @Prop({
    mutable: true,
    reflect: true,
  })
  alt: ResponsiveString;
  /**
   * @type Number
   * @responsive
   */
  @Prop({
    mutable: true,
    reflect: false,
  })
  width: ResponsiveNumber = '16';
  /**
   * @type Number
   * @responsive
   */
  @Prop({
    mutable: true,
    reflect: false,
  })
  height: ResponsiveNumber = '9';

  /**
   * @type String
   * @responsive
   */
  @Prop({
    mutable: true,
    reflect: false,
  })
  aspectRatio: ResponsiveString;
  /**
   * @type Select
   * @options image
   * @options video
   */
  @Prop() type?: 'image' | 'video' = 'image';

  /**
   * @type Select
   * @options video/mp4
   */
  @Prop() videoType?: string = 'video/mp4';

  /**
   * If true, the content in the mediaContent slot will be visible.
   * @type Boolean
   * @responsive
   */
  @Prop({
    mutable: true,
    reflect: true,
  })
  showContent: ResponsiveBoolean = 'false|true';

  @State() lazyLoadIsComplete: boolean = false;
  @State() showVideoAsGif: boolean = false;
  @State() activeLazyLoadClass = '';
  @State() classString = '';
  @State() inlineStyle?: { [key: string]: string } = {};
  @State() hasMediaContent: boolean = false;
  @State() pictureImageSrc: string;
  preloadImage(src) {
    return new Promise(r => {
      const image = new Image();
      image.onload = r;
      image.onerror = r;
      image.src = src;
    });
  }

  componentWillLoad() {
    this.hasMediaContent = !!this.hostElement.querySelector('[slot="mediaContent"]');
    WindowResizeService.registerProp(this.aspectRatio).subscribe(newValue => {
      this.inlineStyle = { ...this.inlineStyle, '--aspect-ratio': `${newValue}` };
    });
    WindowResizeService.registerProp(this.alt).subscribe(newValue => {
      this.alt = newValue as string;
    });
    WindowResizeService.registerProp(this.height).subscribe(newValue => {
      this.height = newValue as string;
    });
    WindowResizeService.registerProp(this.showContent).subscribe(newValue => {
      this.showContent = newValue as string;
    });
    WindowResizeService.registerProp(this.classes).subscribe(newValue => {
      this.classString = `${newValue}`;
    });
  }

  componentDidLoad() {
    let observer = new IntersectionObserver(
      entries => {
        entries.forEach(async entry => {
          if (entry.isIntersecting) {
            this.lazyLoadIsComplete = true;
            this.pictureImageSrc = getCurrentValue(this.src);
            this.showVideoAsGif = this.pictureImageSrc.indexOf('.mp4') >= 0 || getCurrentValue(this.type) === 'video';
          }
        });
      },
      {
        root: null,
        rootMargin: `${Math.max(100, window.innerHeight * 1)}px`,
        // rootMargin: `0px`,
        threshold: 0,
      },
    );
    observer.observe(this.hostElement);
  }

  @Watch('lazyLoadIsComplete')
  watchLazyLoadIsComplete(newValue: boolean) {
    if (newValue === true) {
      this.activeLazyLoadClass = 'content-image-loaded';
    } else {
      if (!this.lazyLoad) {
        this.activeLazyLoadClass = 'content-image-no-lazyload';
      } else {
        this.activeLazyLoadClass = 'content-image-loading';
      }
    }
  }

  render() {
    return (
      <Host class={`${this.activeLazyLoadClass} ${this.classString}`} style={this.inlineStyle} content={this.showContent}>
        {this.hasMediaContent && (
          <div class="content-image__media-content">
            <slot name="mediaContent"></slot>
          </div>
        )}
        {this.showVideoAsGif && (
          <video autoplay muted loop preload="none" playsinline width={this.width} height={this.height}>
            <source src={this.lazyLoadIsComplete ? this.pictureImageSrc : null} type={this.videoType} />
          </video>
        )}
        {this.pictureImageSrc && !this.showVideoAsGif && <img width={this.width} height={this.height} src={this.pictureImageSrc} alt={this.alt} />}
      </Host>
    );
  }
}
